import { useEffect, useRef } from 'react';
import {
  useFieldArray,
  useWatch,
  Control,
  FieldValues,
  UseFieldArrayReturn,
  ArrayPath,
} from 'react-hook-form';

type SortFn<T> = (a: T, b: T) => number;

type UseSortedFieldArrayParams<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
  TKeyName extends string = 'id'
> = {
  control: Control<TFieldValues>;
  name: TName;
  sortFn: SortFn<any>;
  keyName?: TKeyName;
};

type UseSortedFieldArrayReturn<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
  TKeyName extends string = 'id'
> = UseFieldArrayReturn<TFieldValues, TName, TKeyName>;

export function useSortedFieldArray<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
  TKeyName extends string = 'id'
>({
  control,
  name,
  sortFn,
  keyName,
}: UseSortedFieldArrayParams<TFieldValues, TName, TKeyName>): UseSortedFieldArrayReturn<TFieldValues, TName, TKeyName> {
  const fieldArray = useFieldArray<TFieldValues, TName, TKeyName>({
    control,
    name,
    keyName: (keyName ?? 'id') as TKeyName,
  });

  const { move } = fieldArray;
  const watchedValues = useWatch({ control, name } as { control: Control<TFieldValues>; name: TName }) as any[];
  const prevSignatureRef = useRef<string>('');

  const getStableSignature = (items: any[]) =>
    items
      .map((item) => {
        const date = item?.saved?.date;
        return date ? new Date(date).toISOString() : '';
      })
      .join(',');

  useEffect(() => {
    if (!watchedValues || watchedValues.length < 2) return;

    const currentSignature = getStableSignature(watchedValues);

    if (currentSignature === prevSignatureRef.current) return;

    prevSignatureRef.current = currentSignature;

    const indexed = watchedValues.map((item, index) => ({ index, item }));
    const sorted = [...indexed].sort((a, b) => sortFn(a.item, b.item));
    const newOrder = sorted.map((entry) => entry.index);

    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i] !== i) {
        move(newOrder[i], i);
        return; // wait for re-render
      }
    }
  }, [watchedValues, move, sortFn]);

  return fieldArray;
}
